import {
    type ReactElement,
    isValidElement,
    cloneElement,
    Children
} from "react";
import type {
    InputGroupAddonChildrenConstantType,
    InputGroupAddonToolbarGroups
} from "./type";
import type {
    default as IButtonProps
} from "../../../button/type";
import type {
    default as IKbdProps
} from "../../../kbd/type";
import type {
    default as IInputGroupButtonProps
} from "../button/type";
import InputGroupButton from "../button";
import Button from "../../../button";
import Kbd from "../../../kbd";

const getIsKbd = (child: unknown): child is ReactElement<IKbdProps> => {
    return isValidElement(child) && child.type === Kbd;
};

const getIsButton = (child: unknown): child is ReactElement<IInputGroupButtonProps> => {
    return isValidElement(child) && child.type === InputGroupButton;
};

const getIsKitButton = (child: unknown): child is ReactElement<IButtonProps> => {
    return isValidElement(child) && child.type === Button;
};

export const getInputGroupAddonChildren = ({
    isDisabled,
    children,
    kbdSize
}: InputGroupAddonChildrenConstantType) => {
    return Children.toArray(children).map((child) => {
        if(getIsKitButton(child)) {
            return cloneElement(child, {
                spreadBehaviour: child.props.spreadBehaviour ?? "free"
            });
        }

        if(!getIsKbd(child)) {
            return child;
        }

        return cloneElement(child, {
            isDisabled: isDisabled || child.props.isDisabled,
            size: child.props.size ?? kbdSize,
            isInField: true
        });
    });
};

export const getInputGroupAddonToolbarGroups = ({
    isDisabled,
    children,
    kbdSize
}: InputGroupAddonChildrenConstantType): InputGroupAddonToolbarGroups => {
    const groups: InputGroupAddonToolbarGroups = {
        trailing: [],
        leading: [],
        kbds: []
    };

    getInputGroupAddonChildren({
        isDisabled,
        children,
        kbdSize
    }).forEach((child) => {
        if(getIsButton(child)) {
            groups.leading.push(child);

            return;
        }

        if(getIsKbd(child)) {
            groups.kbds.push(child);

            return;
        }

        groups.trailing.push(child);
    });

    return groups;
};

export const getInputGroupAddonIsTrailingTextAction = ({
    children
}: Pick<InputGroupAddonChildrenConstantType, "children">): boolean => {
    const items = Children.toArray(children);

    const lastItem = items[items.length - 1];

    return getIsButton(lastItem) && !lastItem.props.icon;
};
